import React, { useCallback } from 'react';
import { Route, Routes, useNavigate } from 'react-router-dom';
import { conf } from 'config';
import routes from 'store/configs/Routes';
import CommitteeModel from 'store/types/Committee';
import { HTTP_ERROR_CODE } from 'store/types/HttpTypes';
import AffiliateModel from 'store/types/Affiliate';
import { ProfileService } from 'services/ProfileService';
import { LoggerService } from 'services/LoggerService';
import { HttpError } from 'services/HttpService';
import { useAuthContext } from 'AuthContext';
import ModalComponent from 'components/modal/ModalComponent';
import AccountComponent from 'components/account/AccountComponent';
import ProfileFormComponent from './accountForms/ProfileFormComponent';
import LanguageFormComponent from './accountForms/LanguageFormComponent';
import PasswordFormComponent from './accountForms/PasswordFormComponent';
import PrivacyPolicyFormComponent from './accountForms/PrivacyPolicyFormComponent';
import CloseAccountFormComponent from './accountForms/CloseAccountFormComponent';
import MembershipFormComponent from './accountForms/MembershipFormComponent';
import AddressFormComponent from './accountForms/AddressFormComponent';
import PhoneFormComponent from './accountForms/PhoneFormComponent';
import AffiliateFormComponent from 'components/committees/committeesForms/AffiliateFormComponent';
import AddAffiliateFormComponent from 'components/committees/committeesForms/AddAffiliateFormComponent';
import ProfileModel, { PasswordModel } from 'store/types/Profile';
import TechCommitteesFormComponent from 'components/committees/committeesForms/TechCommitteesFormComponent';
import StandardsPrivacyPolicyFormComponent from 'components/committees/committeesForms/StandardsPrivacyPolicyFormComponent';
import StandardsCommitteesFormComponent from 'components/committees/committeesForms/StandardsCommitteesFormComponent';

const logger = LoggerService.getLogger('AccountComponent');

const CommitteesEditStandards = ({ profile, onClose }: { profile: ProfileModel; onClose: () => void }): JSX.Element => {
  const newStandardsPoliciesDate = new Date('06/27/2022');
  let memberAgreementDate = new Date('01/01/1990');
  let noDate = false;

  if (profile.committee.memberAgreeDate === '') {
    noDate = true;
  } else {
    memberAgreementDate = new Date(profile.committee.memberAgreeDate);
  }

  if (!profile.committee.memberAgree || memberAgreementDate < newStandardsPoliciesDate || noDate) {
    return (
      <ModalComponent onClose={onClose} w770={true}>
        <StandardsPrivacyPolicyFormComponent
          onSave={async (updatedProfile: ProfileModel): Promise<void> => {
            await ProfileService.updateCommitteesAgree(
              updatedProfile.committee.standards,
              updatedProfile.committee.memberAgree
            );
            onClose();
          }}
          profileModel={profile}
        />
      </ModalComponent>
    );
  } else {
    return (
      <ModalComponent onClose={onClose} w770={true}>
        <StandardsCommitteesFormComponent
          onSave={async (updatedProfile: ProfileModel): Promise<void> => {
            await ProfileService.updateCommittees(updatedProfile.committee.standards, updatedProfile);
            onClose();
          }}
          profileModel={profile}
        />
      </ModalComponent>
    );
  }
};

const AccountRoutes: React.FunctionComponent = () => {
  const navigate = useNavigate();
  const { profile, refetchProfile } = useAuthContext();

  const closeEdits = useCallback(
    (refetch?: boolean): void => {
      if (refetch) refetchProfile();
      navigate(routes.account.root);
    },
    [navigate, refetchProfile]
  );

  const closeAddAffiliate = useCallback(
    (refetch?: boolean): void => {
      if (refetch) refetchProfile();
      navigate(`${routes.account.root}/${routes.account.committeesEditAffiliate}`);
    },
    [navigate, refetchProfile]
  );

  const saveProfile = useCallback(
    async (profileModel: ProfileModel): Promise<void> => {
      try {
        await ProfileService.updateCustomer(profileModel);
        closeEdits(true);
      } catch (httpError) {
        return Promise.reject(httpError);
      }
    },
    [closeEdits]
  );

  const savePassword = useCallback(
    async (passwordModel: PasswordModel): Promise<void> => {
      const auth0Id = '';
      // TODO: Replace empty string
      try {
        await ProfileService.updatePassword(passwordModel, auth0Id);
        closeEdits(true);
      } catch (httpError) {
        return Promise.reject(httpError);
      }
    },
    [closeEdits]
  );

  const sendAccountClose = useCallback(
    async (_: ProfileModel): Promise<void> => {
      try {
        await ProfileService.deleteCustomer();
        closeEdits(true);
      } catch (httpError) {
        return Promise.reject(httpError);
      }
    },
    [closeEdits]
  );

  const saveCommittees = useCallback(
    async (committees: CommitteeModel[]): Promise<void> => {
      if (!profile) {
        logger.error('Missing profile when trying to update committees.');
        return Promise.reject(new HttpError(HTTP_ERROR_CODE.BAD_REQUEST, conf.defaultErrMessage));
      }

      for (const comm of committees) {
        comm.firstname = profile.customer.firstname;
        comm.lastname = profile.customer.lastname;
        comm.email = profile.customer.email;
      }

      try {
        await ProfileService.updateCommittees(committees, profile);
        closeEdits(true);
      } catch (httpError) {
        return Promise.reject(httpError);
      }
    },
    [closeEdits, profile]
  );

  const saveAffiliate = useCallback(
    async (profileModel: ProfileModel): Promise<void> => {
      try {
        await ProfileService.updateAffiliate(
          profileModel.affiliate.affiliateid || '',
          profileModel.customer.masterID || ''
        );
        closeEdits(true);
      } catch (httpError) {
        return Promise.reject(httpError);
      }
    },
    [closeEdits]
  );

  const addAffiliate = useCallback(
    async (affiliate: AffiliateModel): Promise<void> => {
      try {
        await ProfileService.addAffiliate(affiliate.affiliateName);
        closeAddAffiliate(true);
      } catch (httpError) {
        return Promise.reject(httpError);
      }
    },
    [closeAddAffiliate]
  );

  return (
    <Routes>
      <Route index element={<AccountComponent />} />
      {profile && (
        <>
          <Route
            path={routes.account.editProfile}
            element={
              <ModalComponent onClose={closeEdits}>
                <ProfileFormComponent onSave={saveProfile} profileModel={profile} />
              </ModalComponent>
            }
          />
          <Route
            path={routes.account.editLanguage}
            element={
              <ModalComponent onClose={closeEdits}>
                <LanguageFormComponent onSave={saveProfile} profileModel={profile} />
              </ModalComponent>
            }
          />
          <Route
            path={routes.account.editPassword}
            element={
              <ModalComponent onClose={closeEdits}>
                <PasswordFormComponent onSave={savePassword} />
              </ModalComponent>
            }
          />
          <Route
            path={routes.account.editPrivacyPolicy}
            element={
              <ModalComponent onClose={closeEdits} w770={true}>
                <PrivacyPolicyFormComponent onSave={saveProfile} profileModel={profile} />
              </ModalComponent>
            }
          />
          <Route
            path={routes.account.close}
            element={
              <ModalComponent onClose={closeEdits}>
                <CloseAccountFormComponent onSave={sendAccountClose} profileModel={profile} />
              </ModalComponent>
            }
          />
          <Route
            path={routes.account.editMembership}
            element={
              <ModalComponent onClose={closeEdits}>
                <MembershipFormComponent onSave={saveProfile} profileModel={profile} />
              </ModalComponent>
            }
          />
          <Route
            path={routes.account.editAddress}
            element={
              <ModalComponent onClose={closeEdits}>
                <AddressFormComponent onSave={saveProfile} profileModel={profile} />
              </ModalComponent>
            }
          />
          <Route
            path={routes.account.editPhone}
            element={
              <ModalComponent onClose={closeEdits}>
                <PhoneFormComponent onSave={saveProfile} profileModel={profile} />
              </ModalComponent>
            }
          />
          <Route
            path={routes.account.committeesEditStandards}
            element={<CommitteesEditStandards profile={profile} onClose={closeEdits} />}
          />
          <Route
            path={routes.account.committeesEditTech}
            element={
              <ModalComponent onClose={closeEdits}>
                <TechCommitteesFormComponent
                  onSave={(profile: ProfileModel): Promise<void> => {
                    return saveCommittees(profile.committee.technology);
                  }}
                  profileModel={profile}
                />
              </ModalComponent>
            }
          />
          <Route
            path={routes.account.committeesEditAffiliate}
            element={
              <ModalComponent onClose={closeEdits}>
                <AffiliateFormComponent onSave={saveAffiliate} profileModel={profile} />
              </ModalComponent>
            }
          />
          <Route
            path={routes.account.committeesAddAffiliate}
            element={
              <ModalComponent onClose={closeAddAffiliate}>
                <AddAffiliateFormComponent onSave={addAffiliate} />
              </ModalComponent>
            }
          />
        </>
      )}
    </Routes>
  );
};

export default AccountRoutes;
